/**
 * Where a floating layer goes: beside the element it belongs to — flipped to
 * the other side when only that side has room, then pulled inside the window —
 * and kept there while the page scrolls or resizes.
 *
 * Shared by Popover, HoverCard and a Tooltip beside its element, which differ
 * in how they open and close, not in where they sit; the placement itself is
 * utils/clamp-to-viewport's `placeBeside`, from the anchor's start or, with
 * `align: 'center'`, centred on it. It returns the ref to put on the
 * layer, which is `position: fixed`; its `left` and `top` are written straight
 * onto the element once it has been measured, so a render never waits on a
 * position and the first frame is already in place.
 */

import { useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';

import { placeBeside } from '../../utils/clamp-to-viewport';
import type { Align, Side } from '../../utils/clamp-to-viewport';

/** Between the anchor and the layer. */
const GAP = 8;
/** Between the layer and the window's edge. */
const MARGIN = 8;

export function useAnchoredPosition<T extends HTMLElement>(
  anchor: RefObject<HTMLElement | null>,
  /** Which side of the anchor the layer opens on, aligned to the anchor's start. */
  placement: Side,
  open: boolean,
  align: Align = 'start',
): RefObject<T | null> {
  const layer = useRef<T>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const from = anchor.current?.getBoundingClientRect();
      const element = layer.current;
      if (!from || !element) return;
      const { width, height } = element.getBoundingClientRect();
      const at = placeBeside(from, { width, height }, placement, { gap: GAP, margin: MARGIN, align });
      element.style.left = `${at.x}px`;
      element.style.top = `${at.y}px`;
    };
    place();
    window.addEventListener('resize', place);
    // Capture: a scroll inside any container moves the anchor too.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor, placement, open, align]);
  return layer;
}
